import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, User, Plane, Menu, X,
  FileText, ShieldCheck, MessageCircle, Calendar, BookOpen,
  UtensilsCrossed, Heart, LogOut
} from 'lucide-react';
import { useSiteSettings } from '../SiteSettingsContext';
import { useAuth } from '../App';

const BottomTabBar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { guidePdfUrl } = useSiteSettings();
  const [moreOpen, setMoreOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  const mainTabs = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Home' },
    { path: '/my-trips', icon: Calendar, label: 'Trips' },
    { path: '/food-fun', icon: UtensilsCrossed, label: 'Local Guide' },
    { path: '/profile', icon: User, label: 'Profile' },
  ];

  const moreTabs = [
    { path: '/ask', icon: MessageCircle, label: 'Q&A' },
    { path: '/trip-interest', icon: Heart, label: 'Trip Interest' },
    { path: '/flight-hotel', icon: Plane, label: 'Flight & Hotel' },
    { path: '/waiver', icon: FileText, label: 'Waiver' },
    { path: '/licenses', icon: ShieldCheck, label: 'My Licenses' },
  ];

  // Add guide if available
  const guideLink = guidePdfUrl ? { href: guidePdfUrl, icon: BookOpen, label: 'Guide', external: true } : null;

  return (
    <>
      {/* Backdrop for "More" menu */}
      {moreOpen && (
        <div 
          className="fixed inset-0 bg-black/30 z-40 lg:hidden"
          onClick={() => setMoreOpen(false)}
        />
      )}

      {/* "More" expanded menu */}
      {moreOpen && (
        <div className="fixed bottom-[68px] left-0 right-0 bg-white border-t border-slate-200 rounded-t-2xl shadow-lg z-50 lg:hidden animate-slide-up">
          <div className="p-2 space-y-1">
            {moreTabs.map(({ path, icon: Icon, label }) => (
              <Link
                key={path}
                to={path}
                onClick={() => setMoreOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  isActive(path) 
                    ? 'bg-primary/10 text-primary' 
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium text-sm">{label}</span>
              </Link>
            ))}
            {guideLink && (
              <a
                href={guideLink.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMoreOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 hover:bg-slate-50 transition-colors"
              >
                <BookOpen className="w-5 h-5" />
                <span className="font-medium text-sm">Guide</span>
              </a>
            )}
            <div className="border-t border-slate-100 my-1" />
            <button
              onClick={async () => {
                setMoreOpen(false);
                await logout();
                navigate('/');
              }}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-red-500 hover:bg-red-50 transition-colors w-full"
              data-testid="mobile-sign-out-btn"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium text-sm">Sign Out</span>
            </button>
          </div>
        </div>
      )}

      {/* Bottom Tab Bar */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-50 lg:hidden safe-area-bottom">
        <div className="flex items-center justify-around h-[68px] px-2">
          {mainTabs.map(({ path, icon: Icon, label }) => (
            <Link
              key={path}
              to={path}
              className={`flex flex-col items-center justify-center flex-1 h-full py-2 transition-colors ${
                isActive(path)
                  ? 'text-primary'
                  : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              <Icon className={`w-6 h-6 ${isActive(path) ? 'stroke-[2.5]' : ''}`} />
              <span className={`text-xs mt-1 ${isActive(path) ? 'font-semibold' : 'font-medium'}`}>
                {label}
              </span>
              {isActive(path) && (
                <div className="absolute bottom-1 w-1 h-1 rounded-full bg-primary" />
              )}
            </Link>
          ))}
          
          {/* More button */}
          <button
            onClick={() => setMoreOpen(!moreOpen)}
            className={`flex flex-col items-center justify-center flex-1 h-full py-2 transition-colors ${
              moreOpen ? 'text-primary' : 'text-slate-400 hover:text-slate-600'
            }`}
          >
            {moreOpen ? (
              <X className="w-6 h-6 stroke-[2.5]" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
            <span className={`text-xs mt-1 ${moreOpen ? 'font-semibold' : 'font-medium'}`}>
              More
            </span>
          </button>
        </div>
      </nav>

      {/* Spacer to prevent content from being hidden behind tab bar */}
      <div className="h-[68px] lg:hidden" />

      <style>{`
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.2s ease-out;
        }
        .safe-area-bottom {
          padding-bottom: env(safe-area-inset-bottom, 0);
        }
      `}</style>
    </>
  );
};

export default BottomTabBar;
